import React from 'react';
import { Link } from 'react-router-dom';

const NovaCyclePrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            Nova Cycle by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: 21 April 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Nova Cycle is a period tracking and cycle prediction app developed by Wolf Technologies Ltd.
              We understand that menstrual health data is among the most sensitive personal information you
              can share with an app. We take that responsibility seriously.
            </p>
            <p>
              This Privacy Policy explains what data we collect, how we use it, where it is stored, and
              what rights you have over it. We have designed Nova Cycle with privacy at its core —
              collecting only what is necessary to provide you with accurate cycle predictions and
              meaningful health insights, and nothing more.
            </p>
            <p>
              We encourage you to read this policy in full. If you have any questions, please contact us
              at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <p>
              When you use Nova Cycle, we collect only the data required to deliver the app's core
              functionality:
            </p>
            <ul>
              <li><strong>Cycle dates</strong> — period start and end dates that you log manually.</li>
              <li><strong>Period flow logs</strong> — flow intensity (light, medium, heavy) recorded for each day of your period.</li>
              <li><strong>Symptom data</strong> — physical and emotional symptoms you choose to track (e.g. cramps, headaches, bloating, breast tenderness).</li>
              <li><strong>Mood tracking</strong> — mood entries you record throughout your cycle.</li>
              <li><strong>Cycle notes</strong> — any free-text notes you attach to specific days.</li>
              <li><strong>Account information</strong> (if you sign in) — email address and display name, provided through your chosen sign-in method (email/password or Sign In with Apple).</li>
              <li><strong>Subscription status</strong> — your current plan tier and subscription identifiers, used to unlock paid features.</li>
              <li><strong>Engagement metrics</strong> — aggregated counts of completed cycles and symptom entries, used solely to determine eligibility for subscription prompts.</li>
            </ul>
            <p>
              All health-related data (cycle dates, symptoms, mood, flow, notes) is entered by you
              manually. We do not collect any data automatically from your device's sensors or other apps.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>We want to be explicit about the data we never collect:</p>
            <ul>
              <li><strong>Location data</strong> — we do not access your GPS, Wi-Fi location, or IP-based geolocation.</li>
              <li><strong>Contacts</strong> — we never access your address book or contacts list.</li>
              <li><strong>HealthKit / Apple Health data</strong> — we do not read from or write to Apple HealthKit.</li>
              <li><strong>Device fingerprinting</strong> — we do not collect device identifiers, advertising IDs, or browser fingerprints for tracking purposes.</li>
              <li><strong>Photos or camera data</strong> — we do not access your camera or photo library.</li>
              <li><strong>Microphone or audio</strong> — we do not access your microphone.</li>
              <li><strong>Browsing history</strong> — we do not track your activity outside of Nova Cycle.</li>
              <li><strong>Data from other apps</strong> — we do not access data from any other application on your device.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>The data you provide is used exclusively for the following purposes:</p>
            <ul>
              <li><strong>Cycle predictions</strong> — your logged period dates are used to calculate average cycle length and predict future period start dates, fertile windows, and ovulation estimates.</li>
              <li><strong>Symptom analytics</strong> — symptom and mood entries are analysed to identify patterns across your cycles, helping you understand recurring trends.</li>
              <li><strong>Personalised insights</strong> — as more data is logged, the app refines its predictions and provides increasingly tailored cycle insights.</li>
              <li><strong>Cloud sync</strong> — if you are signed in, your data is synchronised securely to the cloud so you can access it across devices and recover it if you change phones.</li>
              <li><strong>Subscription management</strong> — your account information and subscription status are used to manage your plan and unlock features you have paid for.</li>
              <li><strong>Engagement metrics</strong> — aggregated usage counts (number of completed cycles and symptom entries) are used to determine when to surface upgrade prompts; these are never shared externally.</li>
            </ul>
            <p>
              We do <strong>not</strong> use your data for advertising, profiling, or any purpose unrelated
              to providing and improving the Nova Cycle service.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <p>
              We take the security of your menstrual health data extremely seriously. The following
              measures are in place:
            </p>
            <ul>
              <li><strong>Cloud storage</strong> — all data is stored in Amazon Web Services (AWS) in the eu-west-2 (London) region. Your data does not leave the United Kingdom.</li>
              <li><strong>Encryption at rest</strong> — all data stored in AWS DynamoDB is encrypted at rest using AWS-managed encryption keys (AES-256).</li>
              <li><strong>Encryption in transit</strong> — all communication between the app and our servers uses TLS 1.2 or higher (HTTPS). Data cannot be intercepted in transit.</li>
              <li><strong>Local cache</strong> — a copy of your data is cached locally on your device using an encrypted local database to enable offline access. This cache is stored within the app's sandboxed storage and is not accessible to other apps.</li>
              <li><strong>Authentication</strong> — user accounts are managed through AWS Cognito, which handles password hashing, token management, and secure session handling. We never store your password in plain text.</li>
              <li><strong>Access controls</strong> — your cloud data is partitioned by your unique user ID. No other user can access your data, and our backend enforces this at the API level.</li>
              <li><strong>No server-side logs of health data</strong> — our API does not log the contents of your health entries. Server logs contain only request metadata (timestamps, HTTP status codes) for operational monitoring.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>
              Nova Cycle relies on a small number of trusted third-party services to operate. Each
              receives only the minimum data necessary:
            </p>
            <ul>
              <li><strong>AWS Cognito</strong> — user authentication and account management. Receives email address, hashed password, and authentication tokens.</li>
              <li><strong>AWS DynamoDB</strong> — cloud storage of your cycle and health data. All data you enter in the app is encrypted at rest.</li>
              <li><strong>Stripe</strong> — web payment processing for subscriptions. We never see or store your full card number.</li>
              <li><strong>Apple In-App Purchase</strong> — iOS payment processing for subscriptions. Apple handles all payment details — we receive only a purchase receipt.</li>
            </ul>
            <p>
              We do not use any analytics services, advertising SDKs, social media trackers, or crash
              reporting tools that collect personal data.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <p>
              <strong>We do not sell, rent, trade, or share your personal health data with any third party.</strong>
            </p>
            <p>
              This is a firm commitment. Your menstrual health data — including cycle dates, symptoms, mood
              entries, and notes — is never disclosed to advertisers, data brokers, employers, insurers, or
              any other external party.
            </p>
            <p>The only circumstances in which data may be disclosed are:</p>
            <ul>
              <li><strong>With your explicit consent</strong> — for example, if a future feature allows you to share a cycle report with a healthcare provider, this would only happen at your direct request.</li>
              <li><strong>Legal obligation</strong> — if we are required to do so by law, regulation, or valid legal process (e.g. a court order). We would challenge any such request to the fullest extent permitted by law and notify you where legally permitted.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <ul>
              <li><strong>While your account is active</strong> — your data is retained in the cloud for as long as your account exists, so that you can access your full cycle history and benefit from long-term prediction accuracy.</li>
              <li><strong>Local data</strong> — cached data on your device is retained until you sign out, delete the app, or clear app data.</li>
              <li><strong>Account deletion</strong> — you can delete your account and all associated data at any time through the Settings screen within the app. All data is permanently removed from our cloud database, locally cached data is cleared, and your authentication account is deleted. This action is irreversible.</li>
              <li><strong>Subscription cancellation</strong> — cancelling a subscription does not delete your account or data. Your data is retained and you continue to have access to free-tier features.</li>
              <li><strong>Inactive accounts</strong> — we do not currently delete inactive accounts automatically. If we introduce an inactivity policy in the future, we will notify you by email with at least 90 days' notice before any deletion.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Your Rights (GDPR)</h2>
            <p>
              Under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018,
              you have the following rights:
            </p>
            <ul>
              <li><strong>Access</strong> — you can request a copy of all personal data we hold about you.</li>
              <li><strong>Rectification</strong> — you can correct any inaccurate data by editing your entries in the app, or by contacting us.</li>
              <li><strong>Erasure</strong> — you can delete your account and all associated data through Settings, or by contacting us.</li>
              <li><strong>Data portability</strong> — you can request your data in a structured, commonly used, machine-readable format.</li>
              <li><strong>Restriction</strong> — you can request that we limit how we use your data.</li>
              <li><strong>Objection</strong> — you can object to any processing of your data.</li>
              <li><strong>Withdraw consent</strong> — where processing is based on consent, you can withdraw it at any time.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>. We will
              respond within 30 days, as required by law.
            </p>
            <p>
              If you are not satisfied with our response, you have the right to lodge a complaint with
              the Information Commissioner's Office (ICO) at https://ico.org.uk.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in the app, our
              practices, or legal requirements. When we make changes, the "Last updated" date at the top
              of this page will be revised. For significant changes, we will notify you through the app or
              by email.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or your
              personal data, please contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
            <p>We aim to respond to all enquiries within 48 hours.</p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default NovaCyclePrivacy;
